import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Link as MuiLink,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { FaBell, FaSms, FaClock, FaCalculator } from 'react-icons/fa';
import { startCheckout } from '../services/billingService';
import { useSubscription } from '../hooks/useSubscription';
import { useAuth } from '../auth/AuthProvider';
import supabase from '../config/supabaseClient';

const brandPrimary = '#29458d';
const brandPrimaryHover = '#1e3266';

const primaryBtnSx = {
  backgroundColor: brandPrimary,
  color: '#fff',
  textTransform: 'none',
  fontSize: '16px',
  fontWeight: 500,
  padding: '12px 28px',
  borderRadius: '8px',
  boxShadow: 'none',
  '&:hover': { backgroundColor: brandPrimaryHover, boxShadow: '0 4px 12px rgba(41, 69, 141, 0.3)' },
  '&.Mui-disabled': { backgroundColor: brandPrimary, color: '#fff', opacity: 0.7 },
};

const secondaryBtnSx = {
  color: '#6c757d',
  textTransform: 'none',
  fontSize: '15px',
  fontWeight: 400,
  padding: '12px 20px',
  borderRadius: '8px',
  '&:hover': { backgroundColor: '#f8f9fa', color: brandPrimary },
};

const features = [
  { icon: FaBell, text: 'Therapeutic regimen reminders; schedule & send for every run' },
  { icon: FaSms, text: 'Race-day summary texts right to your phone' },
  { icon: FaClock, text: 'Reminders for each saved horse - tied to your race day schedule' },
  { icon: FaCalculator, text: 'Race calculator & PDF schedule - free with your account' },
];

export default function UpgradeModal({ open, onClose, source, onPhoneNeeded }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const lastClickRef = useRef(0);
  const { subscription } = useSubscription();
  const { user } = useAuth();
  const isResubscribe = !!subscription?.subscription_id;

  const handleStartTrial = async () => {
    // Synchronous guard: ignore re-clicks within 3s to prevent duplicate Stripe sessions
    // from impatient double-clicks (React's setLoading isn't instant).
    const now = Date.now();
    if (now - lastClickRef.current < 3000) return;
    lastClickRef.current = now;

    // Start Free Trial counts as engagement — suppress the save-horse paywall for the
    // same 5-day window as a "Not Now" dismissal. This prevents re-prompting users who
    // bailed out of Stripe checkout and immediately save another horse.
    if (source === 'save_horse') {
      localStorage.setItem('pgp_save_paywall_dismissed_at', String(Date.now()));
    }

    setLoading(true);
    setError(null);
    try {
      // For save_horse source, phone wasn't validated upstream — check now before checkout.
      if (source === 'save_horse' && user?.id) {
        const { data: prof } = await supabase
          .from('profiles')
          .select('phone_number')
          .eq('user_id', user.id)
          .single();
        if (!prof?.phone_number) {
          setLoading(false);
          onPhoneNeeded?.();
          return;
        }
      }

      const { url } = await startCheckout();
      if (url) {
        window.location.href = url;
      } else {
        setError('Could not start checkout. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      if (err.code === 'already_subscribed') {
        setError('You already have an active subscription.');
      } else {
        setError(err.message || 'Could not start checkout. Please try again.');
      }
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header banner */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${brandPrimary} 0%, #1a3270 100%)`,
          px: 3,
          pt: 3.5,
          pb: 3,
          textAlign: 'center',
        }}
      >
        <Typography sx={{ fontSize: '22px', fontWeight: 600, color: '#fff', mb: 0.5 }}>
          Unlock text reminders for your therapeutics & <span style={{ whiteSpace: 'nowrap' }}>race-day</span> prep
        </Typography>
        <Typography sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)', mt: 1 }}>
          Built for barrel racers: prep reminder texts & race-day summaries!
        </Typography>
      </Box>

      <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
        {/* Pricing badge */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 0.5,
            mb: 0.5,
          }}
        >
          <Typography sx={{ fontSize: '28px', fontWeight: 700, color: brandPrimary }}>
            $6.99
          </Typography>
          <Typography sx={{ fontSize: '15px', color: '#6c757d' }}>
            /month
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '14px', color: '#6c757d', mb: 2.5 }}>
          {isResubscribe
            ? 'Billed monthly, plus tax. Cancel anytime.'
            : '30-day free trial, then $6.99/month plus tax. Cancel anytime.'}
        </Typography>

        {/* Feature list */}
        <Box sx={{ mb: 2.5 }}>
          {features.map(({ icon: Icon, text }, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1,
                px: 1.5,
                mb: 1,
                borderRadius: '8px',
                backgroundColor: '#f8f9fc',
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  backgroundColor: 'rgba(41, 69, 141, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon style={{ color: brandPrimary, fontSize: '14px' }} />
              </Box>
              <Typography sx={{ fontSize: '14px', color: '#333' }}>{text}</Typography>
            </Box>
          ))}
        </Box>

        {/* Disclosure */}
        <Box
          sx={{
            backgroundColor: '#fafafa',
            borderRadius: '8px',
            p: 2,
            mb: 1,
          }}
        >
          <Typography sx={{ fontSize: '11px', color: '#888', lineHeight: 1.6 }}>
            By subscribing, you authorize Pre Go Prep to charge your payment method the subscription price shown at
            checkout.{' '}
            {isResubscribe
              ? "You will be charged at the start of each billing period (e.g. monthly) until you cancel, beginning today."
              : "You will not be charged until after your free trial ends. After the trial, you will be charged at the start of each billing period (e.g. monthly) until you cancel."}{' '}
            You may cancel at any time through the subscription or billing management page linked in your profile
            settings; cancellation takes effect at the end of the current billing period. Subscriptions are billed
            monthly unless otherwise specified. We do not provide refunds for any subscription period once it has been
            charged. By completing payment, you agree to our{' '}
            <MuiLink component={RouterLink} to="/terms-of-service" target="_blank" rel="noopener" sx={{ color: brandPrimary, fontSize: '11px' }}>Terms of Service</MuiLink>{' '}
            and{' '}
            <MuiLink component={RouterLink} to="/privacy" target="_blank" rel="noopener" sx={{ color: brandPrimary, fontSize: '11px' }}>Privacy Policy</MuiLink>.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2, fontSize: '14px', borderRadius: '8px' }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'flex-end', gap: 1 }}>
        <Button onClick={onClose} disabled={loading} sx={secondaryBtnSx}>
          Not now
        </Button>
        <Button
          variant="contained"
          onClick={handleStartTrial}
          disabled={loading}
          sx={primaryBtnSx}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {loading ? 'Redirecting…' : isResubscribe ? 'Subscribe' : 'Start Free Trial'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
